import React, { useState, useEffect, useRef } from 'react';
import { decryptFile } from '../services/signalSession';

export default function E2EEVideo({
  fileData,
  senderId,
  currentUserId,
  fileId,
  onLoadedMetadata,
  onClick,
  style,
  controls = true,
  autoPlay = false,
  muted = false,
  loop = false
}) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [failed, setFailed] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!fileData || !senderId || !currentUserId || !fileId) {
      console.error('❌ E2EEVideo : données manquantes', { fileData, senderId, currentUserId });
      setFailed(true);
      return;
    }

    let objectUrl = null;
    let cancelled = false;

    async function loadVideo() {
      try {
        const decrypted = await decryptFile(fileData, senderId, currentUserId, fileId);

        if (!decrypted || decrypted.byteLength === 0) {
          throw new Error('Fichier vidéo déchiffré vide');
        }
        if (cancelled) return;

        const mimeType = fileData.mimeType || 'video/mp4';
        const blob = new Blob([decrypted], { type: mimeType });

        if (blob.size === 0) {
          throw new Error('Blob vidéo vide');
        }

        objectUrl = URL.createObjectURL(blob);

        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        setVideoUrl(objectUrl);

        // ✅ Générer la vignette depuis le blob (local, pas de re-téléchargement)
        const thumb = await generateThumbnail(objectUrl);
        if (!cancelled) setThumbnail(thumb);

      } catch (err) {
        console.error('❌ Erreur déchiffrement vidéo:', fileId, err);
        if (!cancelled) setFailed(true);
      }
    }

    loadVideo();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileData, senderId, currentUserId, fileId]);

// ✅ Lancer la lecture dès que le lecteur est monté
  useEffect(() => {
    if (showPlayer && videoRef.current) {
      videoRef.current.play().catch((err) => {
        // L'autoplay peut être bloqué par le navigateur si la vidéo a du son
        console.warn('Lecture automatique bloquée:', err);
      });
    }
  }, [showPlayer]);

// ✅ Le clic sur la preview déclenche la lecture
  const handlePreviewClick = () => {
    setShowPlayer(true);
  };

  // 🎨 Génération de vignette (même principe que VideoPreview)
  const generateThumbnail = (url) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = url;
      video.muted = true;
      video.currentTime = 1;

      video.addEventListener('loadeddata', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg'));
        video.src = ''; // libérer la ressource
      });

      video.onerror = () => reject('Erreur de chargement vidéo pour vignette');
    });
  };

  if (failed) return null;

  // ⏳ Chargement : déchiffrement en cours
  if (!videoUrl) {
    return (
      <div style={{ padding: '20px', color: '#888', textAlign: 'center' }}>
        Chargement de la vidéo...
      </div>
    );
  }

  // ✅ Lecteur affiché après clic
  if (showPlayer) {
    return (
      <video
	ref={videoRef}
        src={videoUrl}
        controls={controls}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        onLoadedMetadata={onLoadedMetadata}
        onClick={onClick}
        style={style}
        playsInline
      />
    );
  }

  // 🖼️ Mode preview : vignette + bouton play
  return (
    <div
      onClick={handlePreviewClick} 
      style={{
        width: '100%',
        paddingTop: '56.25%',           // ratio 16:9, comme VideoPreview
        position: 'relative',
        backgroundImage: thumbnail ? `url(${thumbnail})` : 'linear-gradient(135deg, #2c3e50, #34495e)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: '8px',
        cursor: 'pointer',
        ...style
      }}
    >
      {/* Bouton play (identique à VideoPreview) */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0,0,0,0.6)',
        borderRadius: '50%',
        width: '60px',
        height: '60px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
    </div>
  );
}